import { ResultOf } from '@graphql-typed-document-node/core';
import classNames from 'classnames';
import { debounce } from 'lodash';
import * as qs from 'qs';
import {
  ElementRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  isRouteErrorResponse,
  useNavigate,
  useOutlet,
  useParams,
  useRouteError,
} from 'react-router-dom';
import { useQuery } from 'urql';
import { useAppContext } from '~/App';
import { RootBoundary } from '~/RootBoundary';
import { Can } from '~/components/Can';
import { Outlet } from '~/components/Split';
import { JobBulkSelectProvider } from '~/components/job/JobBulkSelectContext';
import { BulkEditAction, BulkEditView } from '~/components/job/BulkEditView';
import { JobList } from '~/components/job/JobList/JobList';
import {
  JobListMobileBulkBar,
  type MobileBulkAction,
} from '~/components/job/JobList/JobListMobileBulkBar';
import { Unscheduled } from '~/components/job/JobList/Unscheduled';
import { parseAttributeFilters } from '~/components/layout/CalendarLayout';
import { FilterGroupSearch } from '~/components/ui/FilterGroupSearch';
import { NavContainer } from '~/components/ui/NavContainer';
import { StatefulView } from '~/components/ui/StatefulView';
import { AddButton } from '~/components/ui/buttons/AddButton';
import { Button } from '~/components/ui/buttons/Button';
import { Checkbox, CheckboxState } from '~/components/ui/nucleus/Checkbox';
import { OutletSheet } from '~/components/ui/nucleus/Sheets/OutletSheet';
import { SortBy, SortedBy, sortJobs } from '~/components/ui/sortBy';
import { graphql } from '~/gql';
import {
  CreateJobsMutation,
  JobStatus,
  ListJobsQueryVariables,
} from '~/gql/graphql';
import { remove } from '~/helpers/array';
import {
  clearCreatedJobDates,
  expandDateFiltersToIncludeJobs,
  getRecentlyCreatedJobDates,
} from '~/helpers/jobCreationHints';
import parseFilters from '~/helpers/parseFilters';
import { useLocalStorage } from '~/hooks';
import { useBreakpoint } from '~/hooks/useBreakpoint';
import { useDynamicHeaderMargin } from '~/hooks/useDynamicHeaderMargin';
import { useScrollTrigger } from '~/hooks/useScrollTrigger';
import { useSearch } from '~/hooks/useSearch';
import { JobsNav } from '~/layouts/nav/JobsNav';
import { loadInitialFilters } from './jobListFilterInit';
import { NotFound } from './NotFound';
import { extractAttributes, useSites } from './resources/sites';

const ListJobsDocument = graphql(`
  query ListJobs(
    $scheduled: String
    $dateAfter: DateTime
    $dateBefore: DateTime
    $status: [JobStatus!]
    $locationName: [String!]
    $locationOwnerId: [ID!]
    $locationAttributes: [AttributeInput!]
    $assignee: [ID!]
    $included: [ID!]
    $ofType: String
    $tags: [ID!]
  ) {
    assignees: contacts {
      id
      name
    }
    tags {
      edges {
        node {
          id
          name
          category
          description
          colour
        }
      }
    }
    jobBoard(
      scheduled: $scheduled
      scheduleFrom: $dateAfter
      scheduleTo: $dateBefore
      status: $status
      locationName: $locationName
      locationOwnerId: $locationOwnerId
      locationAttributes: $locationAttributes
      assignee: $assignee
      included: $included
      ofType: $ofType
      tags: $tags
    ) {
      ... on JobCalendarEvent {
        __typename
        id
        hidden
        # some fields are needed for grouping before unmasking:
        summary
        start
        end
        ...JobCalendarEventFields
      }
      ... on Job {
        __typename
        id
        status
        name
        createdAt
        scheduleStart
        scheduleEnd
        scheduledStartStrict
        scheduledStartDate
        scheduledStartTime
        scheduledEndStrict
        scheduledEndDate
        scheduledEndTime
        completedAt
        timeZone
        image
        jobTags {
          id
          entityType
          name
          category
          description
          colour
        }
        location {
          __typename
          id
          name
          ... on Site {
            licensor {
              id
              name
            }
          }
        }
        assignees {
          id
          name
          image
        }
        occupied {
          id
          checkIn: start
          checkOut: end
        }
        prevCheckOut {
          id
          checkIn: start
          checkOut: end
        }
        nextCheckIn {
          id
          checkIn: start
          checkOut: end
        }
      }
    }
  }
`);

export type ViewMode = 'scheduled' | 'unscheduled' | 'completed';

// TJob represents a Job item from the job board list (excluding JobCalendarEvent)
export type TJob = Extract<
  NonNullable<ResultOf<typeof ListJobsDocument>['jobBoard']>[number],
  { __typename: 'Job' }
>;

type TScrollToJob = ElementRef<typeof JobList> & {
  scrollToToday: () => void;
};

const SEARCH_OPTIONS = {
  keys: ['name', 'location.name', 'tags'],
};

// Extract sort options to avoid duplication
const SORT_OPTIONS = [
  { key: SortedBy.TIME, name: 'Time' },
  { key: SortedBy.PROPERTY, name: 'Property' },
  { key: SortedBy.ASSIGNEE, name: 'Assignee' },
];

export function JobsLayout() {
  const { t } = useTranslation(['translation', 'job']);
  const { tenant } = useAppContext();
  const sites = useSites();
  const { view, jobId } = useParams();
  const storage = useLocalStorage({ session: true });
  const [variables, setVariables] = useState<ListJobsQueryVariables>({
    scheduled: view,
  });
  const { isTablet: isSmall, isMobile } = useBreakpoint();
  const jobListRef = useRef<TScrollToJob>(null);
  const navigate = useNavigate();

  // Track view changes
  const previousView = useRef(view);

  useEffect(() => {
    setVariables((v) => ({ ...v, scheduled: view }));
  }, [view]);

  // Simplified hasFilters calculation
  const hasFilters = useMemo(() => {
    const hasArrayFilters = Object.values(variables).some(
      (value) => Array.isArray(value) && value.length > 0
    );
    const hasDateFilters =
      (typeof variables.dateAfter === 'string' && variables.dateAfter !== '') ||
      (typeof variables.dateBefore === 'string' && variables.dateBefore !== '');
    return hasArrayFilters || hasDateFilters;
  }, [variables]);

  const [result] = useQuery({
    query: ListJobsDocument,
    pause: Object.keys(variables).length <= 1, // only fetch when there are filter variables
    requestPolicy: 'cache-and-network',
    variables: {
      ...variables,
    },
  });

  const { data, fetching, error } = result;

  const { results: jobs, search } = useSearch(data?.jobBoard, SEARCH_OPTIONS);

  const [scrollTo, setScrollTo] = useState('');
  useEffect(() => {
    if (fetching) return;
    //! FIXME duplicated in JobsList
    if (scrollTo) {
      document
        .querySelector(`a[href="/jobs/${scrollTo}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [fetching, scrollTo]);

  const scrollToFirst = (data?: CreateJobsMutation) => {
    if (data?.createJobs[0]) {
      setScrollTo(data.createJobs[0].id);
    }
  };

  const [searchValue, setSearchValue] = useState<string>('');
  const [filtersValue, setFiltersValue] = useState<URLSearchParams>(
    loadInitialFilters(storage, view, isMobile, tenant?.contact_id)
  );
  const [sortBy, setSortBy] = useState(
    () => (storage.get('job.sortBy') as SortedBy) || SortedBy.TIME
  );

  // Check for recently created job dates and adjust filters if needed.
  // Uses debounce to prevent duplicate processing from multiple triggers
  // (mount, view change, window focus, visibility change).
  const checkAndAdjustFilters = useMemo(
    () =>
      debounce(
        () => {
          if (view !== 'scheduled' && view !== 'completed') return;

          const recentJobDates = getRecentlyCreatedJobDates();
          if (!recentJobDates) return;

          setFiltersValue((currentFilters) => {
            const expandedFilters = expandDateFiltersToIncludeJobs(
              currentFilters,
              recentJobDates,
              view as 'scheduled' | 'completed'
            );

            if (expandedFilters.toString() !== currentFilters.toString()) {
              storage.set('form.filter.job-list', expandedFilters.toString());
            }
            // Clear hints after processing (whether expanded or not)
            clearCreatedJobDates();
            return expandedFilters;
          });
        },
        200,
        { leading: true, trailing: false }
      ),
    [view, storage]
  );

  // Primary trigger: Check for job hints when filters are loaded or view changes.
  // This covers mount, navigation from onboarding, and view tab switches.
  useEffect(() => {
    if (!isMobile) {
      storage.set('form.filter.job-list', filtersValue.toString());
      return;
    }
    // Only reload filters on view change (not on every render)
    if (previousView.current !== view) {
      previousView.current = view;
      const loadedFilters = loadInitialFilters(
        storage,
        view,
        isMobile,
        tenant?.contact_id
      );
      setFiltersValue(loadedFilters);
    }
  }, [view, storage, isMobile, tenant?.contact_id, filtersValue]);

  // Primary trigger: Check for job hints on mount, view change, and query completion.
  // The debounce prevents duplicate processing within the 200ms window.
  useEffect(() => {
    if (!fetching && (view === 'scheduled' || view === 'completed')) {
      checkAndAdjustFilters();
    }
  }, [fetching, view, checkAndAdjustFilters]);

  // Secondary trigger: Check when window gains focus (catches jobs created in modals).
  // Using visibilitychange alone is sufficient - it fires when tab becomes visible
  // and covers the modal close scenario without needing both focus + visibility listeners.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAndAdjustFilters();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      // Cancel any pending debounced calls on unmount
      checkAndAdjustFilters.cancel();
    };
  }, [checkAndAdjustFilters]);

  // Handle combined filter and search change
  const handleFilterAndSearchChange = useCallback(
    ({
      searchValue: newSearchValue,
      filtersValue: newFilters,
    }: {
      searchValue: string;
      filtersValue: URLSearchParams;
    }) => {
      // Prevent unnecessary updates
      if (
        newSearchValue === searchValue &&
        newFilters.toString() === filtersValue.toString()
      ) {
        return;
      }

      setSearchValue(newSearchValue);

      // Handle user-initiated filter changes
      if (
        view === 'scheduled' ||
        view === 'completed' ||
        view === 'unscheduled'
      ) {
        const oldFilters = filtersValue;

        // Check if any date filters have changed (including being removed)
        if (view === 'scheduled') {
          const hasScheduledDateChanged =
            newFilters.get('scheduledDateAfter') !==
              oldFilters.get('scheduledDateAfter') ||
            newFilters.get('scheduledDateBefore') !==
              oldFilters.get('scheduledDateBefore');

          // Mark scheduled dates as customized when they change
          if (hasScheduledDateChanged) {
            newFilters.set('_scheduledDateCustomized', 'true');
          }
        } else if (view === 'completed') {
          const hasCompletedDateChanged =
            newFilters.get('completedDateAfter') !==
              oldFilters.get('completedDateAfter') ||
            newFilters.get('completedDateBefore') !==
              oldFilters.get('completedDateBefore');

          // Mark completed dates as customized when they change
          if (hasCompletedDateChanged) {
            newFilters.set('_completedDateCustomized', 'true');
          }
        }

        // Check if assignee filter changed
        const assigneeChanged =
          newFilters.get('assignee') !== oldFilters.get('assignee');
        if (assigneeChanged) {
          newFilters.set('_assigneeCustomized', 'true');
        }

        // Mark general customization
        newFilters.set('_userCustomized', 'true');
      }

      try {
        // Save filters before updating state to ensure they persist on navigation
        storage.set('form.filter.job-list', newFilters.toString());
        setFiltersValue(newFilters);
      } catch (error) {
        console.error('Error processing filters:', error);
        setFiltersValue(filtersValue);
      }
    },
    [searchValue, filtersValue, view, storage]
  );

  // Update variables when filters change
  useEffect(() => {
    if (filtersValue && view) {
      try {
        const hasEmptyAssignee =
          filtersValue.has('assignee') && filtersValue.get('assignee') === '';

        const parsedFilters = parseFilters(filtersValue);
        const attributeFilters = parseAttributeFilters(
          parsedFilters['locationAttributes'] || []
        );

        const parsedFiltersWithEmpty = {
          status: (parsedFilters.status ?? []) as JobStatus[],
          locationName: parsedFilters.locationName ?? [],
          assignee: hasEmptyAssignee ? [] : parsedFilters.assignee ?? [],
          included: parsedFilters.included ?? [],
          tags: parsedFilters.tags ?? [],
        };

        let dates = {};
        try {
          dates =
            view === 'scheduled'
              ? {
                  dateAfter: parsedFilters.scheduledDateAfter?.[0] || undefined,
                  dateBefore:
                    parsedFilters.scheduledDateBefore?.[0] || undefined,
                }
              : view === 'completed'
              ? {
                  dateAfter: parsedFilters.completedDateAfter?.[0] || undefined,
                  dateBefore:
                    parsedFilters.completedDateBefore?.[0] || undefined,
                }
              : {};
        } catch (error) {
          console.error('Error parsing date filters:', error);
        }

        setVariables((v) => ({
          ...v,
          ...parsedFiltersWithEmpty,
          ...dates,
          locationAttributes: (attributeFilters || []).map((attr) => ({
            name: attr?.id || '',
            value: attr?.value || '',
          })),
          ofType: '',
        }));

        search(searchValue);
      } catch (error) {
        console.error('Error updating variables from filters:', error);
        setVariables({
          scheduled: view,
        });
      }
    }
  }, [filtersValue.toString(), view, searchValue, search]);

  const filteredJobs = useMemo(() => {
    const bookingFilters = parseFilters(filtersValue).bookingType;
    return jobs?.filter((job) => {
      return (
        job.__typename !== 'JobCalendarEvent' ||
        bookingFilters?.includes(job.summary)
      );
    });
  }, [jobs, filtersValue]);

  const [selected, setSelected] = useState<string[]>([]);
  const [selectMode, setSelectMode] = useState(false);
  const [mobileBulkAction, setMobileBulkAction] =
    useState<BulkEditAction | null>(null);

  const enterSelect = useCallback((initialId?: string) => {
    setSelectMode(true);
    if (initialId) {
      setSelected((prev) =>
        prev.includes(initialId) ? prev : [...prev, initialId]
      );
    }
  }, []);

  const exitSelect = useCallback(() => {
    setSelectMode(false);
    setSelected([]);
    setMobileBulkAction(null);
  }, []);

  const bulkSelectValue = useMemo(
    () => ({ selectMode, enterSelect, exitSelect }),
    [selectMode, enterSelect, exitSelect]
  );

  const toggleSelected = (id: string, shift: boolean, single = false) => {
    // In mobile Select mode, every tap toggles — never replace the selection.
    const replaceSelection = single && !selectMode;
    if (replaceSelection) {
      setSelected([id]);
    } else {
      setSelected((prev) => {
        const idx = prev.findIndex((s) => s === id);

        if (shift && prev.length > 0) {
          const a = data?.jobBoard.findIndex(
            (job) => job.__typename === 'Job' && job.id === id
          );
          const b = data?.jobBoard.findIndex(
            (job) =>
              job.__typename === 'Job' && job.id === prev[prev.length - 1]
          );

          if (a !== undefined && b !== undefined) {
            const from = Math.min(a, b);
            const to = Math.max(a, b);

            return [...Array(1 + to - from)]
              .map((_, i) => {
                const item = data?.jobBoard[i + from];
                return item?.__typename === 'Job' ? item.id : undefined;
              })
              .filter(Boolean);
          }
        }

        return idx > -1 ? remove(prev, idx) : [...prev, id];
      });
    }
  };

  const outlet = useOutlet();
  const selectionProps = useMemo(
    () => ({ selected, toggleSelected }),
    [selected, toggleSelected]
  );

  useEffect(() => {
    setSelected([]);
    setSelectMode(false);
    setMobileBulkAction(null);
  }, [view]);

  useEffect(() => {
    // Always execute this function, use conditional inside
    if (!outlet) {
      setSelected([]);
    }
  }, [outlet]);

  // Loading state management with better initial state handling
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [lastView, setLastView] = useState(view);

  const isLoading = fetching || view !== variables.scheduled;
  const isViewChanging = view !== lastView;

  // Track view changes for loading state
  useEffect(() => {
    if (view !== lastView) {
      setLastView(view);
    }
  }, [view, lastView]);

  // Calculate display state for StatefulView
  const displayState = useMemo(() => {
    if (isLoading || isViewChanging) {
      return isInitialLoad ? 'INITIAL_LOADING' : 'LOADING';
    }
    if (error) {
      setIsInitialLoad(false);
      return 'ERROR';
    }
    if (!data?.jobBoard || data.jobBoard.length === 0) {
      setIsInitialLoad(false);
      return 'NO_RESULTS';
    }
    if (filteredJobs?.length === 0) {
      setIsInitialLoad(false);
      return 'NO_RESULTS';
    }
    setIsInitialLoad(false);
    return 'IDLE';
  }, [
    isLoading,
    isViewChanging,
    error,
    data?.jobBoard,
    filteredJobs,
    isInitialLoad,
  ]);

  // Track when we should trigger scroll to today for scheduled and completed views
  const shouldScrollToToday =
    (view === 'scheduled' || view === 'completed') &&
    !fetching &&
    displayState === 'IDLE';

  // Nav protection - disable scroll detection during loading and active scroll operations (mobile only)
  const [navScrollDetectionEnabled, setNavScrollDetectionEnabled] =
    useState(true);
  const navProtectionTimeoutRef = useRef<NodeJS.Timeout>();
  const [isScrollToTodayActive, setIsScrollToTodayActive] = useState(false);

  // Track when scroll-to-today operations are actually happening
  useEffect(() => {
    // Only apply nav protection on mobile
    if (!isMobile) return;

    if (isLoading || isScrollToTodayActive) {
      // Disable nav scroll detection during loading AND during active scroll-to-today operations
      setNavScrollDetectionEnabled(false);
      if (navProtectionTimeoutRef.current) {
        clearTimeout(navProtectionTimeoutRef.current);
      }
    } else {
      // Keep nav visible for additional time AFTER loading/scrolling completes
      navProtectionTimeoutRef.current = setTimeout(() => {
        setNavScrollDetectionEnabled(true);
      }, 2000); // 2 second protection AFTER operations complete
    }

    return () => {
      if (navProtectionTimeoutRef.current) {
        clearTimeout(navProtectionTimeoutRef.current);
      }
    };
  }, [isLoading, isScrollToTodayActive, isMobile]);

  // Get scroll direction and header margin
  const isScrollingDown = useScrollTrigger();
  const { filterRowRef, dynamicMarginTop, totalHeaderHeight } =
    useDynamicHeaderMargin();

  const exportFilters = qs.stringify(variables);
  const attributeOptions = extractAttributes(sites, data?.assignees);

  const handleCheckboxChange = useCallback(
    (checkboxState: CheckboxState) => {
      const jobsIds = filteredJobs?.map((job) => job.id) || [];
      setSelected(checkboxState === CheckboxState.CHECKED ? jobsIds : []);
    },
    [filteredJobs]
  );

  const sortedJobs = useMemo(() => {
    return sortJobs(sortBy, filteredJobs);
  }, [sortBy, filteredJobs]);

  // Use sorted jobs for all views for consistency
  const viewJobs = view === 'completed' ? filteredJobs : sortedJobs;

  const checkboxState = useMemo(
    () =>
      sortedJobs?.length === selected.length
        ? CheckboxState.CHECKED
        : selected.length > 0
        ? CheckboxState.INDETERMINATE
        : CheckboxState.UNCHECKED,
    [sortedJobs, selected]
  );

  // Calculate active filters/search for StatefulView
  const hasActiveFiltersOrSearch = useMemo(
    () => hasFilters || searchValue.length > 0,
    [hasFilters, searchValue]
  );

  const isSearchResultEmpty = useMemo(
    () =>
      !!searchValue &&
      filteredJobs?.length === 0 &&
      (data?.jobBoard?.length ?? 0) > 0,
    [searchValue, filteredJobs, data?.jobBoard]
  );

  // Desktop only in FilterRow; mobile Select mode uses the bar above the job list.
  const showSelectAll = view !== 'completed' && !isMobile;
  const showMobileSelectBar = isMobile && selectMode;

  const handleMobileBulkAction = useCallback((action: MobileBulkAction) => {
    setMobileBulkAction(action as BulkEditAction);
  }, []);

  const jobsNav = useMemo(() => {
    return (
      <JobsNav selectMode={selectMode} onSelectJobs={() => enterSelect()} />
    );
  }, [selectMode, enterSelect]);

  const showDesktopBulkPanel =
    !isMobile && (selected.length > 1 || (selected.length > 0 && !outlet));
  const showMobileBulkForm = isMobile && selectMode && mobileBulkAction;
  const showMobileBulkBar =
    isMobile && selectMode && selected.length > 0 && !mobileBulkAction;

  return (
    <JobBulkSelectProvider value={bulkSelectValue}>
      <AddButton label='Job' hidden={isMobile && selectMode} />
      <NavContainer
        enableScrollDetection={
          displayState === 'IDLE' &&
          (isMobile ? navScrollDetectionEnabled : true)
        }
        nav={jobsNav}
      >
        {/* Wrapper measured for header margin; Select bar sits under filters and
            hides with the fixed header on scroll. */}
        <div ref={filterRowRef} className='w-full'>
          <NavContainer.FilterRow>
            {showSelectAll && (
              <div className='flex items-center gap-1 pr-2.5'>
                <p
                  className={classNames(
                    'truncate',
                    view === 'unscheduled' ? 'order-2 pr-1' : 'order-1'
                  )}
                >
                  Select All
                </p>
                <div
                  className={classNames(
                    'flex w-6 items-center justify-center',
                    view === 'unscheduled' ? 'order-1 -ml-1.5' : 'order-2'
                  )}
                >
                  <Checkbox
                    value={checkboxState}
                    onClick={(_, value) => handleCheckboxChange(value)}
                  />
                </div>
              </div>
            )}

            <FilterGroupSearch
              sortOptions={SORT_OPTIONS}
              sortBy={sortBy}
              onSortChange={(val) => {
                setSortBy(val);
                storage.set('job.sortBy', val);
              }}
              navScrollDetectionEnabled={navScrollDetectionEnabled}
              filters={[
                ...(view === 'scheduled'
                  ? [
                      {
                        type: 'date' as const,
                        name: 'scheduledDate',
                      },
                    ]
                  : []),
                ...(view === 'completed'
                  ? [
                      {
                        type: 'date' as const,
                        name: 'completedDate',
                      },
                    ]
                  : []),
                {
                  name: 'status',
                  label: t('translation:status'),
                  options: [
                    { value: JobStatus.Created, label: t('job:Created') },
                    {
                      value: JobStatus.InProgress,
                      label: t('job:InProgress'),
                    },
                    { value: JobStatus.Offered, label: t('job:Offered') },
                    { value: JobStatus.Accepted, label: t('job:Accepted') },
                    { value: JobStatus.Declined, label: t('job:Declined') },
                    { value: JobStatus.Cancelled, label: t('job:Cancelled') },
                    { value: JobStatus.Complete, label: t('job:Complete') },
                  ],
                  searchable: false,
                  type: 'select',
                },
                {
                  name: 'locationName',
                  label: t('site'),
                  options:
                    sites
                      .filter(({ status }) => status === 'Active')
                      .map(({ name }) => ({
                        value: name,
                        label: name,
                      })) ?? [],
                  type: 'select',
                },
                {
                  name: 'assignee',
                  label: t('job:assignee'),
                  options:
                    data?.assignees?.map(({ id, name }) => ({
                      value: id,
                      label: name,
                    })) ?? [],
                  type: 'select',
                },
                {
                  name: 'included',
                  label: t('included'),
                  options:
                    data?.assignees?.map(({ id, name }) => ({
                      value: id,
                      label: name,
                    })) ?? [],
                  type: 'select',
                },
                ...(data?.tags && data?.tags.edges.length > 0
                  ? [
                      {
                        name: 'tags',
                        label: t('tag_plural'),
                        options: data.tags.edges
                          .map(({ node }) => ({
                            value: node.id,
                            label: node.name,
                          }))
                          .sort((a, b) => a.label.localeCompare(b.label)),
                        type: 'select' as const,
                      },
                    ]
                  : []),
                {
                  name: 'locationAttributes',
                  label: t('attribute_plural'),
                  options: attributeOptions,
                  type: 'facetedSelect',
                  isAttributeFilter: true,
                },
                ...(view === 'scheduled'
                  ? [
                      {
                        name: 'bookingType',
                        label: t('jobCalendarEvents'),
                        options: [
                          // TODO use translation for labels
                          { value: 'Check-in', label: 'Check-in' },
                          { value: 'Check-out', label: 'Check-out' },
                        ],
                        searchable: false,
                        type: 'select' as const,
                      },
                    ]
                  : []),
              ]}
              value={{ searchValue: searchValue, filtersValue: filtersValue }}
              onChange={handleFilterAndSearchChange}
              placement={'portal'}
            />

            <div className='ml-auto hidden lg:flex'>
              {view === 'scheduled' && (
                <SortBy
                  className='mr-7 rounded-md hover:bg-surface-container-low lg:mr-0'
                  options={SORT_OPTIONS}
                  value={sortBy}
                  onChange={(val) => {
                    setSortBy(val);
                    storage.set('job.sortBy', val);
                  }}
                />
              )}

              <div className='hidden items-center gap-4 py-0.5 md:flex lg:order-last lg:py-0 lg:pl-0 lg:pr-4'>
                <Can do='read' on='feat.export_jobs'>
                  <div className='whitespace-nowrap rounded-md px-3 py-1 text-right hover:bg-surface-container-low'>
                    <a
                      className='text-brand hover:underline'
                      href={`/api/export-jobs?${exportFilters}&view=${view}&format=csv&t=${new Date().valueOf()}`}
                    >
                      {t('export')}
                    </a>
                  </div>
                </Can>
              </div>
            </div>
          </NavContainer.FilterRow>
          {showMobileSelectBar && (
            <div className='flex items-center justify-between gap-3 bg-white px-3 py-2'>
              {view !== 'completed' ? (
                <div className='flex min-w-0 items-center gap-2'>
                  <Checkbox
                    value={checkboxState}
                    onClick={(_, value) => handleCheckboxChange(value)}
                  />
                  <p className='truncate text-sm'>Select All</p>
                </div>
              ) : (
                <span />
              )}
              <Button type='button' color='default' onClick={exitSelect}>
                Cancel Select Jobs
              </Button>
            </div>
          )}
        </div>
      </NavContainer>

      <div
        id='jobs-main'
        style={{
          marginTop: dynamicMarginTop,
        }}
        className={
          'transition-margin w-full bg-surface-container-background duration-200 ease-custom lg:flex lg:h-screen-minus-nav'
        }
      >
        <div className='relative flex-1 lg:overflow-y-hidden' data-split-main>
          <StatefulView
            displayState={displayState}
            activeFilters={hasActiveFiltersOrSearch}
            isSearchResultEmpty={isSearchResultEmpty}
            entityName='jobs'
          >
            {view === 'scheduled' ? (
              <JobList
                key='scheduled-view'
                isLoading={fetching}
                view='scheduled'
                jobs={viewJobs}
                scrollId={jobId}
                hasFilters={hasFilters || searchValue.length > 0}
                isSmall={isSmall}
                ref={jobListRef}
                isScrollingDown={isScrollingDown}
                totalHeaderHeight={
                  isMobile ? totalHeaderHeight + 30 : totalHeaderHeight + 16
                }
                shouldScrollToToday={shouldScrollToToday}
                onScrollToTodayActiveChange={setIsScrollToTodayActive}
                {...selectionProps}
              />
            ) : view === 'unscheduled' ? (
              <Unscheduled
                key='unscheduled-view'
                jobs={filteredJobs}
                scrollId={jobId}
                hasFilters={hasFilters || searchValue.length > 0}
                isSmall={isSmall}
                {...selectionProps}
              />
            ) : view === 'completed' ? (
              <JobList
                key='completed-view'
                isLoading={fetching}
                view='completed'
                jobs={viewJobs}
                scrollId={jobId}
                hasFilters={hasActiveFiltersOrSearch}
                isSmall={isSmall}
                isScrollingDown={isScrollingDown}
                totalHeaderHeight={
                  isMobile ? totalHeaderHeight + 30 : totalHeaderHeight + 16
                }
                shouldScrollToToday={shouldScrollToToday}
                onScrollToTodayActiveChange={setIsScrollToTodayActive}
                ref={jobListRef}
                {...selectionProps}
              />
            ) : null}
          </StatefulView>
        </div>
        {showDesktopBulkPanel ? (
          <BulkEditView selected={selected} onClose={() => setSelected([])} />
        ) : showMobileBulkForm ? (
          <BulkEditView
            selected={selected}
            initialAction={mobileBulkAction}
            onClose={() => {
              setMobileBulkAction(null);
              exitSelect();
            }}
          />
        ) : isMobile && outlet ? (
          <OutletSheet
            isOpen={isMobile && !!outlet}
            onClose={() => {
              navigate(-1);
            }}
          >
            <Outlet
              context={{ scrollToJob: () => jobListRef.current?.scrollToJob() }}
            />
          </OutletSheet>
        ) : (
          <Outlet
            context={{ scrollToJob: () => jobListRef.current?.scrollToJob() }}
            height='lg:h-[calc(100vh-12.5rem)]'
          />
        )}
      </div>
      {showMobileBulkBar && (
        <JobListMobileBulkBar
          selectedCount={selected.length}
          onAction={handleMobileBulkAction}
        />
      )}
    </JobBulkSelectProvider>
  );
}

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }

  return <RootBoundary />;
}
